import React, { useCallback, useEffect, useState } from 'react';
import debounce from 'lodash.debounce';
import * as R from 'ramda';

import client from '@atom/graph/client';
import { GET_USERS } from '@atom/graph/user';
import { GET_USER_GROUPS } from '@atom/graph/userGroup';
import { Autocomplete, Avatar, Chip, Icon, List, Progress } from '@atom/mui';
import colors from '@atom/styles/colors';
import {
  UserDetail,
  UsersConnection,
  UsersConnectionInput,
} from '@atom/types/user';
import {
  UserGroup,
  UserGroupsConnection,
  UserGroupsConnectionInput,
} from '@atom/types/userGroups';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { getColorFromColorId } from '@atom/utilities/colorUtilities';
import { getUserFullName } from '@atom/utilities/userUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

const { ListItemText } = List;

type DebouncedSearch = (name: string, ids?: string[]) => void;
type UserOrGroup = UserDetail | UserGroup;

const DEBOUNCE_TIME = 300;
const MIN_CHAR = 3;
const SEARCH_LIMIT = 250;
const SEARCH_PAGE = 1;

const styles = {
  avatar: {
    marginRight: '0.5rem',
  },
  chip: {
    marginTop: '5px',
    marginRight: '5px',
    backgroundColor: colors.neutral.ash,
    borderRadius: '20px',
  },
};

const isUser = (userOrGroup: UserOrGroup): userOrGroup is UserDetail => {
  return userOrGroup.hasOwnProperty('firstName');
};

const getUserAvatar = (user: UserDetail): React.ReactNode => (
  <Avatar style={styles.avatar} src={user.photoUrl} alt={user.firstName} />
);

const getUserGroupAvatar = (group: UserGroup): React.ReactNode => (
  <Avatar
    style={{
      ...styles.avatar,
      background: getColorFromColorId(group.colorId || 0),
    }}
    alt={group.name}
  >
    <Icon style={{ fontSize: 20 }} color={colors.neutral.white}>
      people
    </Icon>
  </Avatar>
);

interface Props {
  areFiltersDisabled: boolean;
  teamAssignedToGroupIds: string[];
  teamAssignedToUserIds: string[];
  updateAssignedToGroupIds: (updatedGroupIds: string[]) => void;
  updateAssignedToUserIds: (updatedUserIds: string[]) => void;
}

const TeamAssignedToFilter = ({
  areFiltersDisabled,
  teamAssignedToGroupIds,
  teamAssignedToUserIds,
  updateAssignedToGroupIds,
  updateAssignedToUserIds,
}: Props) => {
  const [query, setQuery] = useState<string>('');
  const [loadingSearch, setLoadingSearch] = useState<boolean>(false);
  const [loadingPreselected, setLoadingPreselected] = useState<boolean>(false);
  const [results, setResults] = useState<UserOrGroup[]>([]);
  const [selected, setSelected] = useState<UserOrGroup[]>([]);

  const getUsersAndGroups = async (
    name: string = '',
    userIds: string[] = [],
    userGroupIds: string[] = [],
  ) => {
    const hasIds = userIds?.length || userGroupIds?.length;

    if (hasIds) {
      setLoadingPreselected(true);
    } else {
      setLoadingSearch(true);
    }

    const [users, userGroups] = await Promise.all([
      ...(name || userIds?.length
        ? [
            client
              .query<
                { users: UsersConnection },
                { input: UsersConnectionInput }
              >({
                query: GET_USERS,
                variables: {
                  input: R.reject(isNilOrEmpty, {
                    limit: SEARCH_LIMIT,
                    page: SEARCH_PAGE,
                    name,
                    showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
                    ...(userIds.length && { ids: userIds }),
                  }),
                },
              })
              .then(({ data }) => data.users.users),
          ]
        : [Promise.resolve([])]),
      ...(name || userGroupIds?.length
        ? [
            client
              .query<
                { userGroups: UserGroupsConnection },
                { input: UserGroupsConnectionInput }
              >({
                query: GET_USER_GROUPS,
                variables: {
                  // @ts-ignore
                  input: R.reject(isNilOrEmpty, {
                    limit: SEARCH_LIMIT,
                    page: SEARCH_PAGE,
                    showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
                    name,
                    ...(userGroupIds.length && { ids: userGroupIds }),
                  }),
                },
              })
              .then(({ data }) => data.userGroups.userGroups),
          ]
        : [Promise.resolve([])]),
    ]);

    if (hasIds) {
      setSelected([...users, ...userGroups]);
      setLoadingPreselected(false);
    } else {
      setResults([...users, ...userGroups]);
      setLoadingSearch(false);
    }
  };

  const getUsersAndGroupsDebounced = useCallback<DebouncedSearch>(
    debounce(getUsersAndGroups, DEBOUNCE_TIME),
    [],
  );

  useEffect(() => {
    if (query.length >= MIN_CHAR) {
      getUsersAndGroupsDebounced(query);
    } else {
      setResults([]);
    }
  }, [query]);

  useEffect(() => {
    const hasIds =
      teamAssignedToGroupIds.length || teamAssignedToUserIds.length;
    if (hasIds && !selected.length) {
      getUsersAndGroups(null, teamAssignedToGroupIds, teamAssignedToUserIds);
    }

    if (!hasIds && selected.length) {
      setSelected([]);
    }
  }, [teamAssignedToGroupIds, teamAssignedToUserIds]);

  const handleChange = (values: UserOrGroup[]) => {
    setSelected(values);

    const { userIds, groupIds } = values.reduce(
      (acc, userOrGroup) => {
        return isUser(userOrGroup)
          ? {
              ...acc,
              userIds: [...acc.userIds, userOrGroup.id],
            }
          : {
              ...acc,
              groupIds: [...acc.groupIds, userOrGroup.id],
            };
      },
      { userIds: [], groupIds: [] },
    );

    updateAssignedToGroupIds(groupIds);
    updateAssignedToUserIds(userIds);
  };

  const handleDeselect = (id: string) => {
    setSelected(state => state.filter(user => user.id !== id));

    updateAssignedToGroupIds(
      teamAssignedToGroupIds.filter(groupId => groupId !== id),
    );
    updateAssignedToUserIds(
      teamAssignedToUserIds.filter(userId => userId !== id),
    );
  };

  return (
    <Autocomplete
      multiple
      label="Assigned To"
      options={results}
      loading={loadingSearch}
      disabled={loadingPreselected || areFiltersDisabled}
      inputValue={query}
      onInputChange={(event, val) => setQuery(val || '')}
      getOptionLabel={userOrGroup =>
        isUser(userOrGroup) ? getUserFullName(userOrGroup) : userOrGroup.name
      }
      placeholder={!selected.length ? 'Search for users or groups' : ''}
      value={selected}
      onChange={(event, values) => handleChange(values as UserOrGroup[])}
      getOptionDisabled={userOrGroup =>
        selected.some(({ id }) => id === userOrGroup.id)
      }
      renderOption={(optionProps, userOrGroup) => (
        <li key={userOrGroup.id} {...optionProps}>
          {isUser(userOrGroup)
            ? getUserAvatar(userOrGroup)
            : getUserGroupAvatar(userOrGroup)}
          <ListItemText
            primary={
              isUser(userOrGroup)
                ? getUserFullName(userOrGroup)
                : userOrGroup.name
            }
            secondary={
              isUser(userOrGroup)
                ? userOrGroup.email
                : userOrGroup.groupPath.join(' / ')
            }
          />
        </li>
      )}
      renderValue={usersOrGroups => {
        return usersOrGroups.map(userOrGroup => (
          <Chip
            key={userOrGroup.id}
            style={styles.chip}
            // @ts-ignore
            avatar={
              isUser(userOrGroup)
                ? getUserAvatar(userOrGroup)
                : getUserGroupAvatar(userOrGroup)
            }
            label={
              isUser(userOrGroup)
                ? getUserFullName(userOrGroup)
                : userOrGroup.name
            }
            onDelete={() => handleDeselect(userOrGroup.id)}
          />
        ));
      }}
      groupBy={userOrGroup => (isUser(userOrGroup) ? 'Users' : 'Groups')}
      endAdornment={loadingPreselected && <Progress size={20} />}
    />
  );
};

export default TeamAssignedToFilter;
